import { Matrix } from "../base/matrix";
import { Bounds, mergeBounds } from "../base/bounds";
import { Pt, pt } from "../base/pt";
import { Chain } from "../base/chain";
import { Element } from "./svg";

abstract class Drawing {
  labels: Map<string, Pt>;
  anchorPt?: Pt;
  color: string;
  abstract transform(matrix: Matrix): Drawing;
  abstract label(name: string, pt: Pt): Drawing;
  abstract anchorAt(pt: Pt): Drawing;
  abstract stroke(color: string): Drawing;
  abstract points: Pt[];
  abstract elements: Element[];
  abstract drawings: Drawing[];
  abstract chains: Chain[];

  constructor(color: string, labels: Map<string, Pt>, anchorPt?: Pt) {
    this.color = color;
    this.labels = labels;
    this.anchorPt = anchorPt;
  }

  protected transformLabels(matrix: Matrix): Map<string, Pt> {
    let newLabels = new Map();
    for (let pair of this.labels)
      newLabels.set(pair[0], matrix.transform(pair[1]));
    return newLabels;
  }

  protected transformAnchor(matrix: Matrix): Pt {
    if (this.anchorPt) {
      return matrix.transform(this.anchorPt)
    } else {
      return null;
    }
  }

  pt(i: number | string): Pt {
    if (typeof i == "number") return this.points[i];
    else return this.labels.get(i);
  }

  anchorTo(i: Pt | number | string): Drawing {
    if (typeof i == "object")
      return this.anchorAt(i)
    else
      return this.anchorAt(this.pt(i))
  }

  get bounds(): Bounds {
    return mergeBounds(this.chains.map((c) => c.bounds));
  }

  get anchor(): Pt {
    if (this.anchorPt)
      return this.anchorPt
    else
      return this.points[0];
  }

  anchoredTransform(matrix: Matrix): Drawing {
    const around = this.anchor;
    return this.transform(
      Matrix.translate(around.x, around.y)
        .mul(matrix)
        .mul(Matrix.translate(-around.x, -around.y))
    );
  }

  scale(s: number): Drawing {
    return this.anchoredTransform(Matrix.scale(s, s));
  }

  rotate(degrees: number): Drawing {
    return this.anchoredTransform(Matrix.rotate((degrees * Math.PI) / 180));
  }

  move(x: number, y: number): Drawing {
    return this.transform(Matrix.translate(x, y));
  }

  snap(to: Pt): Drawing {
    const from = this.anchor;
    return this.move(to.x - from.x, to.y - from.y);
  }

  labelAnchor(name: string): Drawing {
    return this.label(name, this.anchor);
  }
}

export { Drawing };
